import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  AlertCircle,
  Clock,
  Info,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  X,
  CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { notificationsApi, leadsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";

const SEVERITY_META = {
  critical: {
    icon: AlertCircle,
    color: "text-red-600 bg-red-50 dark:bg-red-950/40 dark:text-red-300",
    border: "border-red-200 dark:border-red-900/40",
  },
  warning: {
    icon: Clock,
    color: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300",
    border: "border-amber-200 dark:border-amber-900/40",
  },
  info: {
    icon: Info,
    color: "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-300",
    border: "border-blue-200 dark:border-blue-900/40",
  },
};

const TYPE_LABELS = {
  circuit_overdue: { label: "Circuit 24h dépassé", short: "leads HOT non contactés" },
  relance_overdue: { label: "Relances en retard", short: "relances en retard" },
  relance_today: { label: "Relances du jour", short: "relances du jour" },
  rappel_overdue: { label: "Rappels en retard", short: "rappels en retard" },
  rappel_today: { label: "Rappels du jour", short: "rappels du jour" },
  stale: { label: "Leads inactifs", short: "leads inactifs" },
};

// Threshold: when at least N notifications share a type, collapse them in a group.
const GROUP_THRESHOLD = 3;

const seenStorageKey = (projetId) =>
  projetId ? `crm_notif_seen_${projetId}` : "crm_notif_seen_all";

const readSeen = (projetId) => {
  try {
    const raw = localStorage.getItem(seenStorageKey(projetId));
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const writeSeen = (projetId, set) => {
  try {
    localStorage.setItem(seenStorageKey(projetId), JSON.stringify([...set]));
  } catch {}
};

export default function NotificationsDropdown() {
  const navigate = useNavigate();
  const { activeProjectId } = useSettings();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    critical: 0,
    warning: 0,
    info: 0,
    by_type: {},
    truncated: false,
  });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seenIds, setSeenIds] = useState(() => readSeen(activeProjectId));
  // Track expanded groups (when user clicks "Voir les X autres")
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationsApi.feed(activeProjectId);
      setItems(data.items || []);
      setCounts(data.counts || { total: 0, critical: 0, warning: 0, info: 0, by_type: {} });
    } catch (e) {
      // ignore — global axios interceptor already humanizes the error
    } finally {
      setLoading(false);
    }
  }, [activeProjectId]);

  // Reload seen ids when project changes
  useEffect(() => {
    setSeenIds(readSeen(activeProjectId));
  }, [activeProjectId]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  // Filter out seen items
  const visibleItems = useMemo(
    () => items.filter((n) => !seenIds.has(n.id)),
    [items, seenIds]
  );

  // Group identical types when >= GROUP_THRESHOLD
  const grouped = useMemo(() => {
    const buckets = {};
    visibleItems.forEach((n) => {
      const key = n.type || "other";
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(n);
    });

    const result = [];
    Object.entries(buckets).forEach(([type, group]) => {
      if (group.length >= GROUP_THRESHOLD) {
        result.push({ kind: "group", type, items: group });
      } else {
        group.forEach((n) => result.push({ kind: "single", item: n }));
      }
    });
    // Sort: critical groups/items first, then by count desc for groups
    const severityOf = (entry) =>
      entry.kind === "group"
        ? entry.items[0]?.severity || "info"
        : entry.item?.severity || "info";
    const sevOrder = { critical: 0, warning: 1, info: 2 };
    result.sort((a, b) => {
      const sa = sevOrder[severityOf(a)] ?? 9;
      const sb = sevOrder[severityOf(b)] ?? 9;
      if (sa !== sb) return sa - sb;
      const ca = a.kind === "group" ? -a.items.length : 0;
      const cb = b.kind === "group" ? -b.items.length : 0;
      return ca - cb;
    });
    return result;
  }, [visibleItems]);

  const visibleCount = visibleItems.length;
  const hiddenCount = items.length - visibleCount;
  const visibleCritical = visibleItems.filter((n) => n.severity === "critical").length;
  const visibleWarning = visibleItems.filter((n) => n.severity === "warning").length;
  const visibleInfo = visibleItems.filter((n) => n.severity === "info").length;

  const badgeColor = visibleCritical > 0
    ? "bg-red-500"
    : visibleWarning > 0
    ? "bg-amber-500"
    : "bg-[hsl(var(--accent-navy))]";

  // Local cache (instant UI) + backend sync (persistence cross-device).
  // The backend filter is the source of truth on next reload — localStorage
  // just covers the optimistic-UI gap between click and refetch.
  const markAllSeen = async () => {
    const ids = items.map((n) => n.id);
    const next = new Set(seenIds);
    ids.forEach((id) => next.add(id));
    setSeenIds(next);
    writeSeen(activeProjectId, next);
    try {
      await notificationsApi.markSeen(ids);
    } catch {
      /* ignore — local state already updated */
    }
  };

  const restoreAll = async () => {
    const empty = new Set();
    setSeenIds(empty);
    setExpanded(new Set());
    writeSeen(activeProjectId, empty);
    try {
      await notificationsApi.restore();
      load();
    } catch {
      /* ignore */
    }
  };

  const markOneSeen = async (id) => {
    const next = new Set(seenIds);
    next.add(id);
    setSeenIds(next);
    writeSeen(activeProjectId, next);
    try {
      await notificationsApi.markSeen([id]);
    } catch {
      /* ignore */
    }
  };

  const handleNotifClick = (n) => {
    markOneSeen(n.id);
    setOpen(false);
    if (n.lead_id) navigate(`/leads/${n.lead_id}`);
  };

  /** Push a relance's `prochaine_action` to tomorrow and dismiss the notif.
   * Resolves the underlying problem instead of just hiding it — so the same
   * notification doesn't pop right back up after the next refresh. */
  const postponeOneDay = async (notif) => {
    if (!notif?.lead_id) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const ymd = tomorrow.toISOString().slice(0, 10);
    try {
      await leadsApi.update(notif.lead_id, { prochaine_action: ymd });
      await markOneSeen(notif.id);
      toast.success(`Relance reportée à demain (${ymd})`);
      load();
    } catch (e) {
      toast.error("Erreur lors du report");
    }
  };

  const toggleGroup = (type) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="notifications-btn"
          title={`${visibleCount} notification${visibleCount > 1 ? "s" : ""}`}
          className="relative touch-target"
        >
          <Bell className={cn("h-4 w-4", visibleCritical > 0 && "text-red-500")} />
          {visibleCount > 0 && (
            <span
              data-testid="notifications-badge"
              className={cn(
                "absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold text-white",
                badgeColor
              )}
            >
              {visibleCount > 99 ? "99+" : visibleCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[min(94vw,400px)] p-0"
        data-testid="notifications-panel"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2 border-b border-border/60 px-4 py-3">
          <div className="min-w-0">
            <div className="font-heading text-sm font-semibold">Notifications</div>
            <div className="text-[11px] text-muted-foreground">
              {/* Always render every non-empty severity bucket. The previous
                  layout collapsed `info` whenever critical or warning was
                  set, which meant a 2-critical + 1-info badge showed total
                  count = 3 but the label only said "2 urgentes" — the info
                  notification became invisible despite the badge. */}
              {(() => {
                const parts = [];
                if (visibleCritical > 0) {
                  parts.push(
                    <span key="c" className="font-medium text-red-600">
                      {visibleCritical} urgente{visibleCritical > 1 ? "s" : ""}
                    </span>
                  );
                }
                if (visibleWarning > 0) {
                  parts.push(
                    <span key="w" className="font-medium text-amber-600">
                      {visibleWarning} à traiter
                    </span>
                  );
                }
                if (visibleInfo > 0) {
                  parts.push(<span key="i">{visibleInfo} info</span>);
                }
                if (parts.length === 0) {
                  return <span>Tout est à jour</span>;
                }
                return parts.reduce(
                  (acc, p, i) => (i === 0 ? [p] : [...acc, " · ", p]),
                  []
                );
              })()}
            </div>
          </div>
          <div className="flex flex-none items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllSeen}
              disabled={visibleCount === 0}
              className="h-7 px-2 text-[11px]"
              data-testid="notifications-mark-all"
              title="Marquer toutes comme vues"
            >
              Tout vu
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={restoreAll}
              disabled={hiddenCount === 0}
              className="h-7 px-2 text-[11px]"
              data-testid="notifications-restore"
              title="Réafficher les notifications masquées"
            >
              Réafficher
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={load}
              disabled={loading}
              className="h-7 w-7"
              data-testid="notifications-refresh"
              title="Actualiser"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Body */}
        {visibleCount === 0 ? (
          <EmptyState hiddenCount={hiddenCount} onRestore={restoreAll} />
        ) : (
          <ol className="max-h-[440px] overflow-y-auto" data-testid="notifications-list">
            {grouped.map((entry) =>
              entry.kind === "group" ? (
                <NotifGroup
                  key={`g-${entry.type}`}
                  type={entry.type}
                  items={entry.items}
                  expanded={expanded.has(entry.type)}
                  onToggle={() => toggleGroup(entry.type)}
                  onClickItem={handleNotifClick}
                  onDismiss={markOneSeen}
                  onPostpone={postponeOneDay}
                />
              ) : (
                <NotifItem
                  key={entry.item.id}
                  notif={entry.item}
                  onClick={() => handleNotifClick(entry.item)}
                  onDismiss={() => markOneSeen(entry.item.id)}
                  onPostpone={() => postponeOneDay(entry.item)}
                />
              )
            )}
          </ol>
        )}

        {counts.truncated && (
          <div className="border-t border-border/40 bg-muted/30 px-4 py-2 text-center text-[10px] text-muted-foreground">
            Affichage limité à 50 notifications · {counts.raw_total} au total
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

function EmptyState({ hiddenCount, onRestore }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      <CheckCircle2 className="h-8 w-8 text-emerald-500/70" />
      <span className="text-sm text-muted-foreground">Tout est à jour</span>
      {hiddenCount > 0 && (
        <Button
          variant="link"
          size="sm"
          onClick={onRestore}
          className="h-auto p-0 text-[11px]"
          data-testid="notifications-restore-empty"
        >
          Réafficher les {hiddenCount} masquée{hiddenCount > 1 ? "s" : ""}
        </Button>
      )}
    </div>
  );
}

function NotifItem({ notif, onClick, onDismiss, onPostpone }) {
  const meta = SEVERITY_META[notif.severity] || SEVERITY_META.info;
  const Icon = meta.icon;
  // "Reporter à demain" only makes sense for relance notifs (the ones whose
  // root cause is `prochaine_action`).
  const canPostpone =
    !!notif.lead_id &&
    (notif.type === "relance_overdue" || notif.type === "relance_today");

  const stopAnd = (fn) => (e) => {
    e.stopPropagation();
    fn?.();
  };

  return (
    <li
      data-testid={`notification-item-${notif.id}`}
      onClick={onClick}
      className={cn(
        "group flex cursor-pointer items-start gap-3 border-b border-border/40 px-4 py-3 last:border-b-0 transition-colors hover:bg-muted/40",
        meta.border
      )}
    >
      <div className={cn("flex h-8 w-8 flex-none items-center justify-center rounded-full", meta.color)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-heading truncate text-sm font-semibold pr-12">
          {notif.title}
        </div>
        {notif.message && (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {notif.message}
          </p>
        )}
      </div>
      {/* Action buttons : visible at all times on mobile, on hover on desktop */}
      <div className="flex flex-none flex-col items-center gap-0.5 opacity-100 sm:opacity-60 sm:group-hover:opacity-100 transition-opacity">
        {canPostpone && (
          <button
            type="button"
            onClick={stopAnd(onPostpone)}
            className="rounded-md p-1 text-muted-foreground hover:bg-emerald-100 hover:text-emerald-700 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-300"
            title="Reporter à demain"
            data-testid={`notification-postpone-${notif.id}`}
          >
            <CalendarPlus className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={stopAnd(onDismiss)}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Masquer cette notification"
          data-testid={`notification-dismiss-${notif.id}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}

function NotifGroup({ type, items, expanded, onToggle, onClickItem, onDismiss, onPostpone }) {
  const meta = SEVERITY_META[items[0]?.severity] || SEVERITY_META.info;
  const Icon = meta.icon;
  const label = TYPE_LABELS[type]?.short || type;
  const count = items.length;
  return (
    <li
      className={cn("border-b border-border/40 last:border-b-0", meta.border)}
      data-testid={`notification-group-${type}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40"
        aria-expanded={expanded}
        data-testid={`notification-group-toggle-${type}`}
      >
        <div className={cn("flex h-8 w-8 flex-none items-center justify-center rounded-full", meta.color)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-sm font-semibold">
            {count} {label}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {expanded ? "Cliquer pour replier" : "Cliquer pour voir le détail"}
          </p>
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 flex-none text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 flex-none text-muted-foreground" />
        )}
      </button>
      {expanded && (
        <ol className="bg-muted/20 pl-4">
          {items.map((n) => (
            <NotifItem
              key={n.id}
              notif={n}
              onClick={() => onClickItem(n)}
              onDismiss={() => onDismiss?.(n.id)}
              onPostpone={() => onPostpone?.(n)}
            />
          ))}
        </ol>
      )}
    </li>
  );
}
